import { useState } from "react";
import { Bell, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useNotifications,
  useMarkNotificationAsRead,
  useDeleteNotification,
} from "@/hooks/Notification";
import type { NotificationItem } from "@/shared/types/Notifications";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useNotifications();
  const markAsReadMutation = useMarkNotificationAsRead();
  const deleteMutation = useDeleteNotification();

  const notifications = data ?? [];
  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const handleMarkAsRead = (n: NotificationItem) => {
    if (n.isRead) return;
    markAsReadMutation.mutate(n.id);
  };

  const handleDelete = (n: NotificationItem) => {
    deleteMutation.mutate(n.id);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Ver notificaciones"
          className="shrink-0 relative min-h-[44px] min-w-[44px] transition-all duration-150 hover:bg-muted text-foreground"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 min-w-[20px] rounded-full bg-[#ef3f47] text-[10px] font-bold text-white flex items-center justify-center shadow-md">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="w-80 max-h-[420px] overflow-y-auto"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notificaciones</span>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unreadCount} sin leer
            </Badge>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {isLoading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex gap-2">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            No tienes notificaciones
          </div>
        ) : (
          notifications.map((n) => (
            <DropdownMenuItem
              key={n.id}
              className={`group flex items-start gap-2 px-3 py-2 text-sm ${!n.isRead ? "bg-muted/60" : ""} hover:bg-[rgb(239,63,71)] transition-colors duration-500`}
              onSelect={(e) => e.preventDefault()}
            >
              <div className="mt-1">
                {!n.isRead ? (
                  <span className="inline-flex h-2 w-2 rounded-full bg-[rgb(239,63,71)] group-hover:bg-white" />
                ) : (
                  <Check className="h-3 w-3 text-muted-foreground group-hover:text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium group-hover:text-white">{n.title}</p>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap group-hover:text-white">
                    {format(new Date(n.createdAt), "dd/MM HH:mm", { locale: es })}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 group-hover:text-white">
                  {n.message}
                </p>
                <div className="mt-1 flex gap-2">
                  {!n.isRead && (
                    <Button
                      variant="link"
                      className="h-6 text-xs px-2 group-hover:text-white"
                      onClick={() => handleMarkAsRead(n)}
                    >
                      Marcar como leída
                    </Button>
                  )}
                  <Button
                    variant="link"
                    className="h-6 text-xs px-2 text-[rgb(239,63,71)] group-hover:text-white"
                    onClick={() => handleDelete(n)}
                  >
                    <Trash2 className="h-3 w-3 mr-1 group-hover:text-white" />
                    Eliminar
                  </Button>
                </div>
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
